import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import { formatPrice } from '@spotahome/soyuz-currency';

import { ListingCostsShape } from '../../constants/shapes';

import { DSIcon } from '../../../base';

import InfoBanner from '../InfoBanner';

import { getUnit, isEstimatedCost } from '../PaymentTimeline/costHelpers';

import DetailRow from './DetailRow';
import PaymentDetailsCard from './PaymentDetailsCard';

import styles from './PaymentDetailsCard.module.scss';

const DEPOSIT_TYPE = 'deposit';

const BeforeMoveIn = ({
  hasNoDeposit = false,
  costs = {},
  currencyCode,
  isCollapsed = true
}) => {
  const costList = costs?.costs ?? [];
  const hasContent =
    costList.length > 0 ||
    (typeof costs?.total === 'number' && costs.total > 0);

  if (!hasContent) {
    return null;
  }

  const totalValue = formatPrice(costs.total, currencyCode);
  const depositCost = costList.find(c => c.type === DEPOSIT_TYPE);
  const otherCosts = costList.filter(c => c.type !== DEPOSIT_TYPE);

  const renderDetailRowForCost = cost => {
    const unit = getUnit(cost.applyOn);
    const estimatedSubtitle = isEstimatedCost(cost.applyOn)
      ? trans('payment.timeline.estimated-cost')
      : null;
    const formattedBase =
      typeof cost.value === 'number'
        ? formatPrice(cost.value, currencyCode)
        : cost.value;
    const displayValue =
      unit && typeof cost.value === 'number'
        ? trans(`price.per.${unit}`, { price: formattedBase })
        : formattedBase;

    return (
      <DetailRow
        key={cost.type}
        label={cost.name ?? trans(`payment.timeline.cost.label.${cost.type}`)}
        tooltipText={cost.description || null}
        value={displayValue}
        compareValueForStrike={
          typeof cost.value === 'number' ? cost.value : null
        }
        valueWithoutOffer={cost.valueWithoutOffer}
        currencyCode={currencyCode}
        valueSubtitle={estimatedSubtitle}
      />
    );
  };

  return (
    <PaymentDetailsCard
      dataTest="payment-details.before-move-in"
      isCollapsed={isCollapsed}
      title={trans('payment.details.before_move_in.title')}
      headerValue={totalValue}
    >
      {() => (
        <>
          {depositCost ? (
            <DetailRow
              label={
                depositCost.name ??
                trans(`payment.timeline.cost.label.${depositCost.type}`)
              }
              tooltipText={depositCost.description || null}
              value={depositCost.value}
              valueWithoutOffer={depositCost.valueWithoutOffer}
              currencyCode={currencyCode}
              labelSubtitle={
                !hasNoDeposit
                  ? trans('payment.details.before_move_in.deposit_refundable')
                  : null
              }
            />
          ) : null}

          {depositCost && !hasNoDeposit ? (
            <InfoBanner
              contentClassName={styles['payment-details-card__info-banner']}
              closable={false}
              content={trans('payment.details.before_move_in.deposit_banner')}
              dataTest="payment-details.before-move-in.deposit-banner"
              icon={<DSIcon name="lock_closed" size="m" />}
              theme="info"
            />
          ) : null}

          {otherCosts.map(cost => renderDetailRowForCost(cost))}
        </>
      )}
    </PaymentDetailsCard>
  );
};

BeforeMoveIn.propTypes = {
  hasNoDeposit: PropTypes.bool,
  costs: ListingCostsShape,
  currencyCode: PropTypes.string.isRequired,
  isCollapsed: PropTypes.bool
};

export default BeforeMoveIn;
